import { AVIS_VERIFIES_URL_REVIEWS, NUMBER_OF_REVIEWS } from '~/constants';
import type { Reviews } from '~/types/Reviews';

import { avisVerifiesTokens } from '~/helpers/reviewsRating';
import { useLocalization } from '~/composables/_next/useLocalization';

export function useReviews() {
  const { locale } = useLocalization();
  /**
   * Takes an array of raw review data and transforms it into a structured Reviews object format.
   */
  function transformReviews(reviewsData: any): Reviews[] {
    return reviewsData.map((review: any) => {
      return {
        rating: review.rate,
        description: review.review,
        firstName: review.firstname,
        lastName: review.lastname,
        date: review.review_date,
        customerServiceResponse: review.moderation,
      };
    });
  }

  /**
   * Fetches reviews for a specific product ID with pagination
   */
  async function fetchReviews(ids: string[], offset: number): Promise<Reviews[] | null> {
    try {
      const results = await $fetch(AVIS_VERIFIES_URL_REVIEWS, {
        method: 'POST',
        body: JSON.stringify({
          idWebsite: avisVerifiesTokens[locale],
          query: 'reviews',
          order: 'date_desc',
          products: ids,
          limit: NUMBER_OF_REVIEWS,
          plateforme: locale,
          offset: (offset - 1) * 3,
        }),
      });

      return results ? transformReviews(results[0]?.reviews) : null;
    } catch (error) {
      console.warn('[useReviews] - Could not deserialize reviews', error);
    }
  }

  /**
   * Fetches average review data for a specific product ID (and optional locale).
   */
  async function fetchAverageReviews(ids: string[], localeOpt?: string) {
    try {
      return await $fetch(AVIS_VERIFIES_URL_REVIEWS, {
        method: 'POST',
        body: JSON.stringify({
          idWebsite: avisVerifiesTokens[localeOpt ?? locale],
          query: 'average',
          products: ids,
          plateforme: localeOpt ?? locale,
        }),
        redirect: 'follow',
      });
    } catch (error) {
      console.warn('[useReviews] - Could not deserialize average reviews', error);
    }
  }

  /**
   * Fetches average review data for a specific product ID across all supported locales.
   */
  async function fetchAllAverageReviews(ids: string[]) {
    const results = {};
    const responses = await Promise.all(
      Object.keys(avisVerifiesTokens).map((locale) => fetchAverageReviews(ids, locale).then((result) => result)),
    );

    for (const response of responses) {
      if (!response) continue;
      for (const responseId in response) {
        if (!results[responseId]) {
          results[responseId] = { count: 0, totalRate: 0 };
        }
        const item = response[responseId][0];
        if (item) {
          results[responseId].count += Number(item.count);
          results[responseId].totalRate += Number(item.rate) * Number(item.count); // Multiply rate by count to get total
        }
      }
    }

    for (const resultId in results) {
      const item = results[resultId];
      item.averageRate = item.count ? item.totalRate / item.count : 0;
      delete item.totalRate;
    }

    return results;
  }

  return {
    fetchReviews,
    fetchAverageReviews,
    fetchAllAverageReviews,
  };
}
